import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { v4 as uuidv4 } from 'uuid';

import localUtil from '../../lib/util';
import { renderNewsletterHtml } from '../../lib/newsletterRender';
import { articlePageCategories } from '../../lib/articleLinks';
import BlockEditor from './blockEditor';

const BLOCK_TYPES = ['hero', 'text', 'article', 'image', 'button', 'divider', 'highlight'];

function isValidEmail(input) {
    const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return pattern.test(input);
}

@observer
class NewsletterBuilder extends Component {
    constructor(props) {
        super(props);
        this.state = {
            draftId: undefined,
            name: '',
            subject: '',
            previewText: '',
            blocks: [],
            saving: false,
            savedAt: null,
            sending: false,
            message: '',
            error: '',
        };
        this.saveTimer = null;
    }

    componentDidMount() {
        const { newsletterStore } = this.props.stores;
        newsletterStore.loadDrafts();
        if (this.props.draftId > 0) {
            this.loadDraftById(this.props.draftId);
        }
    }

    componentWillUnmount() {
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
    }

    getBranding() {
        const { appState, userStore } = this.props.stores;
        // Brand the newsletter as the clinic the user is ACTING AS (userStore.customer),
        // not the domain/hostname owner (appState.currentCustomer).
        const c = userStore.customer || appState.currentCustomer || {};
        const logoImg = Array.isArray(c.images) && c.images.length > 0 ? c.images[0] : null;
        const hostname = (typeof window !== 'undefined' && window.location && window.location.hostname) || 'dyrejournal.app';
        // Public article pages are a property of the DOMAIN owner (the hostname the links
        // point at), so the flag and category list come from appState.currentCustomer.
        const domainFlag = appState.currentCustomer?.featureFlags?.articlePages;
        return {
            logoUrl: logoImg ? localUtil.displayImg(logoImg, this.props, '400x') : '',
            accentColor: c.backgroundColorHeader || '#1f8a8a',
            hostname,
            articlePages: domainFlag === 1 || domainFlag === true,
            articlePageCategories: articlePageCategories(appState),
            footer: {
                name: c.name || '',
                address: [c.address, [c.postalcode, c.place].filter(Boolean).join(' ')].filter(Boolean).join(', '),
                email: c.email || '',
                phone: c.phone || '',
                orgNr: c.orgNumber || '',
            },
        };
    }

    scheduleSave = () => {
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
        this.setState({ saving: true });
        this.saveTimer = setTimeout(this.doSave, 750);
    }

    doSave = async () => {
        const { newsletterStore } = this.props.stores;
        const {
            draftId, name, subject, previewText, blocks,
        } = this.state;
        const saved = await newsletterStore.saveDraft({
            id: draftId, name, subject, previewText, blocks,
        });
        if (saved && saved.id) {
            this.setState({ draftId: saved.id, saving: false, savedAt: Date.now() });
            newsletterStore.loadDrafts();
        } else {
            this.setState({ saving: false });
        }
    }

    handleHeaderField = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value }, this.scheduleSave);
    }

    addBlock = (e) => {
        const { type } = e.currentTarget.dataset;
        const block = { uuidv4: uuidv4(), type };
        if (type === 'divider') {
            block.style = 'line';
        }
        if (type === 'highlight') {
            block.rows = [{ title: '', text: '' }];
        }
        this.setState(prev => ({ blocks: prev.blocks.concat([block]) }), this.scheduleSave);
    }

    changeBlock = (blockUuidv4, key, value) => {
        this.setState(prev => ({
            blocks: prev.blocks.map(b => (b.uuidv4 === blockUuidv4 ? { ...b, [key]: value } : b)),
        }), this.scheduleSave);
    }

    pickArticleForBlock = (blockUuidv4, article) => {
        const slug = encodeURIComponent(String(article.title || 'artikkel')).toLowerCase();
        const imageUrl = Array.isArray(article.images) && article.images.length > 0
            ? localUtil.displayImg(article.images[0], this.props, '400x')
            : '';
        this.setState(prev => ({
            blocks: prev.blocks.map(b => (b.uuidv4 === blockUuidv4 ? {
                ...b,
                articleId: article.id,
                articleUuidv4: article.uuidv4,
                articleSlug: slug,
                articleCategory: article.category || '',
                title: article.title || '',
                ingress: article.ingress || '',
                imageUrl,
            } : b)),
        }), this.scheduleSave);
    }

    removeBlock = (blockUuidv4) => {
        this.setState(prev => ({ blocks: prev.blocks.filter(b => b.uuidv4 !== blockUuidv4) }), this.scheduleSave);
    }

    moveBlock = (blockUuidv4, dir) => {
        this.setState(prev => {
            const blocks = prev.blocks.slice();
            const idx = blocks.findIndex(b => b.uuidv4 === blockUuidv4);
            const swap = idx + dir;
            if (idx < 0 || swap < 0 || swap >= blocks.length) {
                return null;
            }
            const tmp = blocks[idx];
            blocks[idx] = blocks[swap];
            blocks[swap] = tmp;
            return { blocks };
        }, this.scheduleSave);
    }

    moveUp = blockUuidv4 => this.moveBlock(blockUuidv4, -1);

    moveDown = blockUuidv4 => this.moveBlock(blockUuidv4, 1);

    loadDraft = async (e) => {
        const id = parseInt(e.target.value, 10);
        if (!id) {
            return;
        }
        await this.loadDraftById(id);
    }

    loadDraftById = async (id) => {
        const { newsletterStore } = this.props.stores;
        const doc = await newsletterStore.loadDraft(id);
        if (doc) {
            this.setState({
                draftId: doc.id,
                name: doc.name || '',
                subject: doc.subject || '',
                previewText: doc.previewText || '',
                blocks: Array.isArray(doc.blocks) ? doc.blocks : [],
                savedAt: Date.now(),
            });
        }
    }

    sendNewsletter = async () => {
        const { appState } = this.props.stores;
        const { subject, blocks, previewText } = this.state;

        if (!subject || blocks.length === 0) {
            this.setState({ error: 'Fyll inn emne og minst én blokk.' });
            return;
        }

        // Guard against a double-click racing two POSTs while draftId is still
        // unset (both clicks would otherwise take the isNew branch in saveDraft).
        if (this.state.sending) {
            return;
        }
        this.setState({ sending: true, error: '', message: '' });

        // Flush the debounced autosave so a draft id exists before we send.
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
            this.saveTimer = null;
        }
        await this.doSave();

        const { draftId } = this.state;
        if (!draftId) {
            this.setState({ sending: false, error: 'Kunne ikke lagre utkastet. Prøv igjen.' });
            return;
        }

        const { userStore } = this.props.stores;
        const branding = this.getBranding();
        // previewText is the inbox preview line the admin typed; it has to reach the renderer
        // or it is collected and thrown away.
        const { html, text } = renderNewsletterHtml(blocks, branding, { previewText });
        const { recipients = [] } = this.props;

        const level = appState.getTopMostDrawer() + 1;
        appState.openDrawer('newsletterSendDrawer', {
            height: appState.drawerHeightMediumLarge,
            newsletterId: draftId,
            subject,
            bodyHtml: html,
            bodyText: text,
            hostname: branding.hostname,
            fromEmail: userStore.customer?.email || appState.currentCustomer?.email || '',
            users: recipients.filter(u => u.id > 0).map(u => u.id),
            adhocRecipients: recipients
                .filter(u => !(u.id > 0) && u.email)
                .map(u => ({ email: u.email, customerId: u.customerId })),
        }, level);
        this.setState({ sending: false });
    }

    render() {
        const { recipients = [] } = this.props;
        const { newsletterStore } = this.props.stores;
        const {
            name, subject, previewText, blocks, saving, sending, message, error,
        } = this.state;
        const drafts = newsletterStore.newsletters || [];

        const branding = this.getBranding();
        // Same call shape as the send path above, so the preview iframe stays literally what
        // recipients receive — preheader included.
        const { html } = renderNewsletterHtml(blocks, branding, { previewText });

        const validRecipients = recipients.filter(u => isValidEmail(u.email));
        const skipped = validRecipients.filter(u => u.newsletterUnsubscribed);

        return (
            <div class='d-flex flex-column overflow-auto mb-5 mt-3 p-3'>
                <div class='font-weight-lighter px-1 box-header mb-2'>
                    <i class='fa-solid fa-newspaper me-2' />
                    <Text id='newsletter.title'>Newsletter builder</Text>
                </div>

                <div class='row g-2 mb-3'>
                    <div class='col-12 col-md-6'>
                        <input class='form-control' name='name' value={name} onInput={this.handleHeaderField} placeholder='Navn på utkast (internt)' />
                    </div>
                    <div class='col-12 col-md-6'>
                        <select class='form-select' onChange={this.loadDraft}>
                            <option value=''>— Last inn utkast —</option>
                            {drafts.map(d => (
                                <option key={d.id} value={d.id}>{d.name || `Utkast ${d.id}`} {d.status === 'sent' ? '(sendt)' : ''}</option>
                            ))}
                        </select>
                    </div>
                    <div class='col-12'>
                        <input class='form-control' name='subject' value={subject} onInput={this.handleHeaderField} placeholder='Emne (subject)' />
                    </div>
                    <div class='col-12'>
                        <input class='form-control' name='previewText' value={previewText} onInput={this.handleHeaderField} placeholder='Forhåndsvisningstekst (preheader)' />
                    </div>
                </div>

                <div class='d-flex flex-row align-items-center justify-content-between mb-2'>
                    <span class='text-body-secondary'>
                        <Text id='newsletter.blocks'>Blocks</Text>
                    </span>
                    <span class='text-body-secondary small'>
                        {saving
                            ? <span><span class='spinner-border spinner-border-sm me-1' /> <Text id='newsletter.saving'>Saving…</Text></span>
                            : <Text id='newsletter.autosaved'>Changes are saved automatically</Text>}
                    </span>
                </div>

                <div class='d-flex flex-wrap gap-1 mb-3'>
                    {BLOCK_TYPES.map(type => (
                        <button key={type} type='button' class='btn btn-sm btn-outline-primary rounded-pill' data-type={type} onClick={this.addBlock}>
                            <i class='fa-solid fa-plus me-1' />{type}
                        </button>
                    ))}
                </div>

                {blocks.map((block, index) => (
                    <BlockEditor
                        key={block.uuidv4}
                        stores={this.props.stores}
                        block={block}
                        index={index}
                        total={blocks.length}
                        onChange={this.changeBlock}
                        onPickArticle={this.pickArticleForBlock}
                        onRemove={this.removeBlock}
                        onMoveUp={this.moveUp}
                        onMoveDown={this.moveDown}
                    />
                ))}

                <div class='font-weight-lighter px-1 box-header mt-4 mb-2'>
                    <i class='fa-solid fa-eye me-2' />
                    <Text id='newsletter.preview'>Preview</Text>
                </div>
                <iframe title='newsletter-preview' srcDoc={html} style='width:100%;height:520px;border:1px solid #ddd;border-radius:10px;background:#fff;' />

                {recipients.length > 0 && (
                    <div class='d-flex flex-column px-1 mt-3 mb-2'>
                        <span class='text-body-secondary'>
                            <Text id='newsletter.recipients'>Recipients</Text>: {validRecipients.length}
                        </span>
                        {skipped.length > 0 && (
                            <span class='text-warning small'>
                                <Text id='newsletter.skipped-unsubscribed'>Will be skipped (unsubscribed)</Text>: {skipped.length}
                            </span>
                        )}
                    </div>
                )}

                <button
                    type='button'
                    class='btn btn-primary rounded-pill mt-2'
                    onClick={this.sendNewsletter}
                    disabled={sending || !subject || blocks.length === 0}
                >
                    <i class='fa-solid fa-paper-plane me-1' /> <Text id='newsletter.send'>Send newsletter</Text>
                    {sending && <span class='spinner-border spinner-border-sm ms-2' role='status' aria-hidden='true' />}
                </button>

                {!!error && <div class='alert alert-danger rounded mt-3'>{error}</div>}
                {!!message && <div class='alert alert-success rounded mt-3'>{message}</div>}
            </div>
        );
    }
}

export default NewsletterBuilder;
